import React, { useEffect, useMemo, useState } from "react";
import MenuPage from "./MenuPage";

const StatusCard = ({ title, description }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#0a1224] via-[#0b152d] to-[#050b18] text-white flex items-center justify-center px-6">
    <div className="w-full max-w-lg rounded-3xl bg-white/5 border border-white/10 p-6 shadow-2xl">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
          <img
            src="/moo-sync-insigne-logo.png"
            alt="MOO Sync"
            className="h-7 w-7 object-contain"
          />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/60">MOO Sync</p>
          <h1 className="text-xl font-semibold">{title}</h1>
        </div>
      </div>
      <p className="mt-4 text-white/70">{description}</p>
      <p className="mt-6 text-xs text-white/40">
        Si tu penses que c’est une erreur, contacte le support Djizef SARL.
      </p>
    </div>
  </div>
);

export default function CloudTenantMenu() {
  const [state, setState] = useState({ status: "loading", tenant: null });

  const origin = useMemo(() => {
    try {
      return window.location.origin;
    } catch {
      return "";
    }
  }, []);

  const hostname = useMemo(() => {
    try {
      return window.location.hostname || "";
    } catch {
      return "";
    }
  }, []);

  const subdomain = useMemo(() => {
    const parts = String(hostname || "").split(".").filter(Boolean);
    if (parts.length < 3) return "";
    return parts[0] || "";
  }, [hostname]);

  const baseDomainOrigin = useMemo(() => {
    const parts = String(hostname || "").split(".").filter(Boolean);
    if (parts.length < 3) return "";
    return `https://${parts.slice(1).join(".")}`;
  }, [hostname]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const qs = subdomain ? `?subdomain=${encodeURIComponent(subdomain)}` : "";
        const primaryUrl = `/api/cloud/public/tenant${qs}`;
        const fallbackUrl =
          baseDomainOrigin && baseDomainOrigin !== origin
            ? `${baseDomainOrigin}/api/cloud/public/tenant${qs}`
            : "";

        let res;
        try {
          res = await fetch(primaryUrl, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
        } catch (_e) {
          if (!fallbackUrl) throw _e;
          res = await fetch(fallbackUrl, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
        }
        if (cancelled) return;

        if (res.status === 404) {
          setState({ status: "not_found", tenant: null });
          return;
        }
        if (!res.ok) {
          // Best-effort fallback if the API is served on the base domain.
          if (fallbackUrl) {
            try {
              const alt = await fetch(fallbackUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
              });
              if (cancelled) return;
              if (alt.status === 404) {
                setState({ status: "not_found", tenant: null });
                return;
              }
              if (alt.ok) {
                const data = await alt.json();
                const tenant = data?.tenant || null;
                if (tenant && (tenant.status || "").toLowerCase() === "active") {
                  setState({ status: "ok", tenant });
                  return;
                }
                if (tenant) {
                  setState({ status: "inactive", tenant });
                  return;
                }
              }
            } catch (_e) {
              // ignore fallback errors
            }
          }
          setState({ status: "error", tenant: null });
          return;
        }

        const data = await res.json();
        const tenant = data?.tenant || null;
        if (!tenant) {
          setState({ status: "error", tenant: null });
          return;
        }
        if ((tenant.status || "").toLowerCase() !== "active") {
          setState({ status: "inactive", tenant });
          return;
        }

        setState({ status: "ok", tenant });
      } catch (_e) {
        if (!cancelled) setState({ status: "error", tenant: null });
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1224] via-[#0b152d] to-[#050b18] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
          <div className="h-9 w-9 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
            <img
              src="/moo-sync-insigne-logo.png"
              alt="MOO Sync"
              className="h-5 w-5 object-contain"
            />
          </div>
          <div className="text-sm text-white/70">Chargement du menu…</div>
        </div>
      </div>
    );
  }

  if (state.status === "not_found") {
    return (
      <StatusCard
        title="Établissement introuvable"
        description={`Ce sous-domaine n’est pas (ou plus) associé à un établissement. (${origin})`}
      />
    );
  }

  if (state.status === "inactive") {
    return (
      <StatusCard
        title="Établissement non connecté"
        description="Cet établissement n’est pas connecté au cloud pour le moment."
      />
    );
  }

  if (state.status === "error") {
    return (
      <StatusCard
        title="Service indisponible"
        description="Impossible de vérifier l’établissement pour le moment. Réessaye dans quelques instants."
      />
    );
  }

  return <MenuPage />;
}
